import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
var USER_ROLE = {
    User: 0,
    ReportReader: 1,
    GlobalAdministrator: 2,
    ConfigurationEditor: 3,
    ScopedAdministrator: 4
};
// Discriminator values for `User.userType`. Mirrors backend `constants/userConstants.js`
// + `models/user.js`. Always check EXTERNAL — legacy rows lack the field and fall through.
var USER_TYPE = {
    MICROSOFT_365: 'microsoft365',
    EXTERNAL: 'external'
};
var _obj;
var ANALYTICS_USER_TYPE = (_obj = {}, _define_property(_obj, USER_TYPE.MICROSOFT_365, USER_TYPE.MICROSOFT_365), _define_property(_obj, USER_TYPE.EXTERNAL, 'non_microsoft'), _obj);
/**
 * Translate a stored `User.userType` into the analytics vocabulary.
 *
 * `userType` is optional because legacy rows predate the field (see USER_TYPE above) and the
 * schema getter that defaults it does NOT run on `.lean()` reads — an unguarded lookup would
 * report `undefined` for exactly those users.
 *
 * @param   {UserType} [userType] - Stored discriminator; absent on legacy rows.
 * @returns {AnalyticsUserType} `non_microsoft` for external, else `microsoft365`.
 * @example analyticsUserType(user.userType) // 'non_microsoft'
 */ var analyticsUserType = function analyticsUserType(userType) {
    return ANALYTICS_USER_TYPE[userType !== null && userType !== void 0 ? userType : USER_TYPE.MICROSOFT_365];
};
var userAttributeDataType = {
    NUMBER: 'NUMBER',
    TEXT: 'TEXT',
    DATE: 'DATE',
    CURRENCY: 'CURRENCY',
    DROPDOWN: 'DROPDOWN',
    ICON: 'ICON'
};
var currencySymbols = {
    AED: 'د.إ',
    AFN: '؋',
    ALL: 'L',
    AMD: '֏',
    ANG: 'ƒ',
    AOA: 'Kz',
    ARS: '$',
    AUD: '$',
    AWG: 'ƒ',
    AZN: '₼',
    BAM: 'KM',
    BBD: '$',
    BDT: '৳',
    BGN: 'лв',
    BHD: '.د.ب',
    BIF: 'Fr',
    BMD: '$',
    BND: '$',
    BOB: 'Bs.',
    BOV: 'BOV',
    BRL: 'R$',
    BSD: '$',
    BTN: 'Nu.',
    BWP: 'P',
    BYR: 'Br',
    BZD: '$',
    CAD: '$',
    CDF: 'Fr',
    CHE: 'CHE',
    CHF: 'Fr',
    CHW: 'CHW',
    CLF: 'CLF',
    CLP: '$',
    CNY: '¥',
    COP: '$',
    COU: 'COU',
    CRC: '₡',
    CUC: '$',
    CUP: '$',
    CVE: '$',
    CZK: 'Kč',
    DJF: 'Fr',
    DKK: 'kr',
    DOP: '$',
    DZD: 'د.ج',
    EGP: '£',
    ERN: 'Nfk',
    ETB: 'Br',
    EUR: '€',
    FJD: '$',
    FKP: '£',
    GBP: '£',
    GEL: '₾',
    GHS: '₵',
    GIP: '£',
    GMD: 'D',
    GNF: 'Fr',
    GTQ: 'Q',
    GYD: '$',
    HKD: '$',
    HNL: 'L',
    HRK: 'kn',
    HTG: 'G',
    HUF: 'Ft',
    IDR: 'Rp',
    ILS: '₪',
    INR: '₹',
    IQD: 'ع.د',
    IRR: '﷼',
    ISK: 'kr',
    JMD: '$',
    JOD: 'د.ا',
    JPY: '¥',
    KES: 'KSh',
    KGS: 'лв',
    KHR: '៛',
    KMF: 'Fr',
    KPW: '₩',
    KRW: '₩',
    KWD: 'د.ك',
    KYD: '$',
    KZT: '₸',
    LAK: '₭',
    LBP: '£',
    LKR: '₨',
    LRD: '$',
    LSL: 'L',
    LTL: 'Lt',
    LVL: 'Ls',
    LYD: 'ل.د',
    MAD: 'د.م.',
    MDL: 'L',
    MGA: 'Ar',
    MKD: 'ден',
    MMK: 'K',
    MNT: '₮',
    MOP: 'P',
    MRO: 'UM',
    MUR: '₨',
    MVR: 'Rf',
    MWK: 'MK',
    MXN: '$',
    MXV: 'MXV',
    MYR: 'RM',
    MZN: 'MT',
    NAD: '$',
    NGN: '₦',
    NIO: 'C$',
    NOK: 'kr',
    NPR: '₨',
    NZD: '$',
    OMR: 'ر.ع.',
    PAB: 'B/.',
    PEN: 'S/.',
    PGK: 'K',
    PHP: '₱',
    PKR: '₨',
    PLN: 'zł',
    PYG: 'Gs',
    QAR: 'ر.ق',
    RON: 'lei',
    RSD: 'дин.',
    RUB: '₽',
    RWF: 'Fr',
    SAR: 'ر.س',
    SBD: '$',
    SCR: '₨',
    SDG: 'ج.س.',
    SEK: 'kr',
    SGD: '$',
    SHP: '£',
    SLL: 'Le',
    SOS: 'Sh',
    SRD: '$',
    SSP: '£',
    STD: 'Db',
    SYP: '£',
    SZL: 'L',
    THB: '฿',
    TJS: 'ЅМ',
    TMT: 'm',
    TND: 'د.ت',
    TOP: 'T$',
    TRY: '₺',
    TTD: '$',
    TWD: 'NT$',
    TZS: 'Sh',
    UAH: '₴',
    UGX: 'Sh',
    USD: '$',
    USN: 'USN',
    USS: 'USS',
    UYI: 'UYI',
    UYU: '$',
    UZS: 'лв',
    VEF: 'Bs F',
    VND: '₫',
    VUV: 'Vt',
    WST: 'T',
    XAF: 'Fr',
    XAG: 'oz t',
    XAU: 'oz t',
    XBA: 'XBA',
    XBB: 'XBB',
    XBC: 'XBC',
    XBD: 'XBD',
    XCD: '$',
    XDR: 'SDR',
    XFU: 'XFU',
    XOF: 'Fr',
    XPD: 'oz t',
    XPF: 'Fr',
    XPT: 'oz t',
    XTS: 'XTS',
    XXX: 'XXX',
    YER: '﷼',
    ZAR: 'R',
    ZMW: 'ZK'
};
var defaultLabelToKey = {
    Department: 'department',
    'Job Title': 'jobTitle',
    'Office Location': 'officeLocation',
    Country: 'country',
    'Employee Hire Date': 'employeeHireDate',
    Birthday: 'birthday'
};
var CONFIG_AND_ADMIN_ROLES = [
    USER_ROLE.GlobalAdministrator,
    USER_ROLE.ConfigurationEditor,
    USER_ROLE.ScopedAdministrator
];
var defaultKeyToLabel = Object.fromEntries(Object.entries(defaultLabelToKey).map(function(param) {
    var _param = _sliced_to_array(param, 2), label = _param[0], key = _param[1];
    return [
        key,
        label
    ];
}));
var FOCUS_PEOPLE_STATUS = {
    PENDING: 1,
    ACCEPTED: 2,
    REJECTED: 0,
    AUTO_CREATED: 3 // auto created by 1-on-1 meetings
};
var ATTRIBUTE_INPUT_TYPE = {
    ATTRIBUTE: 'ATTRIBUTE'
};
var ATTRIBUTE_DATA_SOURCE = {
    MANUAL_INPUT: 'MANUAL_INPUT',
    SYNC_FROM_365: 'SYNC_FROM_365',
    SYNC_FROM_3RD_PARTY: 'SYNC_FROM_3RD_PARTY',
    SYNC_FROM_ENTRA_ID: 'SYNC_FROM_ENTRA_ID'
};
var ATTRIBUTE_VISIBILITY = {
    ALL: 'ALL',
    USER_MANAGER_ADMIN: 'USER_MANAGER_ADMIN',
    MANAGER_ADMIN: 'MANAGER_ADMIN',
    ADMIN: 'ADMIN',
    NOT_VISIBLE: 'NOT_VISIBLE'
};
var DEFAULT_MANAGER_INFORMATION_SOURCE = {
    SYNC_FROM_365: ATTRIBUTE_DATA_SOURCE.SYNC_FROM_365,
    SYNC_FROM_3RD_PARTY: ATTRIBUTE_DATA_SOURCE.SYNC_FROM_3RD_PARTY
};
export { USER_ROLE, USER_TYPE, ANALYTICS_USER_TYPE, analyticsUserType, CONFIG_AND_ADMIN_ROLES, userAttributeDataType, currencySymbols, defaultLabelToKey, defaultKeyToLabel, FOCUS_PEOPLE_STATUS, ATTRIBUTE_INPUT_TYPE, ATTRIBUTE_DATA_SOURCE, ATTRIBUTE_VISIBILITY, DEFAULT_MANAGER_INFORMATION_SOURCE };
